package com.dynamicruntime.webapp

import com.dynamicruntime.common.user.PERSONA
import com.dynamicruntime.common.user.personaTerm
import com.dynamicruntime.common.user.USF
import com.dynamicruntime.common.user.userSearchFieldSpecs
import com.dynamicruntime.common.context.UserProfile
import react.FC
import react.Props
import react.create
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import kotlin.time.Instant

/**
 * The user search results as an antd table (issue #411). The **search columns are rendered from the shared
 * spec** ([userSearchFieldSpecs]): their existence, order, header label, and sortability all come from the
 * kernel description, so a field added to the spec becomes a column here with no edit -- the SDUI extra credit.
 * Clicking a sortable header drives a **server-side** re-sort through [onSort], so the order stays correct even
 * when the match ran past the endpoint's cap. The descriptive columns after them (Type, Status) are
 * static -- they are not search fields.
 *
 * Presentational: every value and the current sort arrive via props. Mirrors [EndpointTable], which solves the
 * same "pick one from a list" problem.
 */
external interface UserTableProps : Props {
    var users: List<AdminUser>
    var onSelect: (AdminUser) -> Unit

    /**
     * Whether to show which client each user belongs to (issue #352) -- the same rule the create selector and
     * the client filter follow. A spec field marked `allClientsOnly` is shown only when this is true, since the
     * client is one value to anyone who cannot see more than one.
     */
    var showClient: Boolean

    /** The field currently sorted on -- a [USF] sort key -- so the active column shows its arrow. */
    var sortBy: String

    /** Whether the current sort is descending, for the active column's arrow direction. */
    var descending: Boolean

    /** Reports a header-click sort: the [USF] sort key and whether it is now descending. */
    var onSort: (field: String, descending: Boolean) -> Unit

    /**
     * Whether each row offers the way to its user's forms and client (issue #1081, [userLinksOffered]): a column
     * of links, so neither is a detour through another page's filters.
     */
    var showLinks: Boolean

    /**
     * Whether those links include the user's **client** (issue #1432): not for an editor, who reads the client's users
     * and forms but none of its configuration, which is what the Clients page shows. Null reads as true.
     */
    var showClientLinks: Boolean?

    /** The clients this node carries, as the page was told them -- which a Forms link may choose ([userFormsSurface]). */
    var carriedClients: Set<String>

    /** Whether a row offers to become its user (issue #1511, [becomeOffered]); null for a caller who is offered none. */
    var offersBecome: ((AdminUser) -> Boolean)?

    /** [BecomeLinkProps.disabled]. */
    var becomeDisabled: Boolean?

    /** [BecomeLinkProps.onOutcome]. */
    var onBecomeOutcome: (BecomeFailure?) -> Unit
}

val UserTable = FC<UserTableProps> { props ->
    // What the date columns read their relative times against (issue #1141): one clock for every row.
    val now = useRelativeClock()
    Table {
        size = "small"
        // Declared widths mean what they say (see `TableProps.tableLayout`): under the default auto layout a
        // long email address grew past its own column and took the space from the date columns, which have a
        // fixed-width content that cannot give way.
        tableLayout = "fixed"
        pagination = false
        rowKey = "key"
        val cols = buildList {
            add(column("Id", idColumn, columnWidths[idColumn]))
            // The search columns, straight from the shared spec -- label, sortability, and order included.
            for (spec in userSearchFieldSpecs) {
                if (spec.allClientsOnly && !props.showClient) continue
                val c = if (spec.sortable) {
                    sortableColumn(spec.label, spec.name, columnWidths[spec.name], props.sortBy, props.descending)
                } else {
                    column(spec.label, spec.name, columnWidths[spec.name])
                }
                // A date column's cell carries its exact time on hover (issue #1141), filed beside it on the row.
                if (spec.name in userTimeFields) c.render = timeColumnRender(timeTipKey(spec.name))
                add(c)
            }
            // Descriptive columns that are not search fields, so they stay static and unsorted.
            add(column("Type", typeColumn, columnWidths[typeColumn]))
            add(column("Status", statusColumn, columnWidths[statusColumn]))
            if (props.showLinks || props.offersBecome != null) add(userLinksColumn(props))
        }
        // The columns that identify a user stay in view while the rest scroll (issue #750): who this is on the
        // left, and whether they are live on the right. antd pins a column only when the table has an explicit
        // horizontal extent, so `scroll.x` is the declared widths' sum -- under it the middle scrolls inside the
        // card; above it the surplus is shared out as before.
        for (c in cols) {
            pinnedSide(c.dataIndex as String)?.let { c.fixed = it }
        }
        columns = cols.toTypedArray()
        val total = cols.sumOf { (it.width as? Int) ?: 0 }
        scroll = js("({ x: total })")
        dataSource = props.users.map { user ->
            val row: dynamic = js("({})")
            row.key = user.userId.toString()
            row.userId = user.userId.toString()
            // A search column's cell is filed under the spec's field name (which is also its `sorter.field`), so
            // the header maps straight to a sort key. The display value per field is the frontend's own reading
            // of the row -- the counterpart to the backend's accessors.
            for (spec in userSearchFieldSpecs) {
                row[spec.name] = cellValue(spec.name, user, now)
            }
            for ((field, at) in userTimes(user)) at?.let { row[timeTipKey(field)] = formatTimestamp(it) }
            row.type = if (user.isEntity) "Business" else "Person"
            row.status = statusWords(user).joinToString(", ")
            row
        }.toTypedArray()
        // A header click re-sorts on the server. antd's cycle on a sortable column is ascend, descend, then
        // *none* -- the third click, which its tooltip announces as "Click to cancel sorting", hands back a null
        // order. Cancelling means the default order, not no order (the listing always has one), so that click
        // goes back to the default key and direction rather than being dropped, which is what it used to be.
        onChange = { _, _, sorter ->
            sortAfterHeaderClick(sorter.field as? String, sorter.order as? String)?.let { (field, descending) ->
                props.onSort(field, descending)
            }
        }
        // The whole row is the selection target; look the user back up by the key the row carries.
        onRow = { record, _ ->
            val handlers: dynamic = js("({})")
            handlers.onClick = {
                props.users.firstOrNull { it.userId.toString() == record.key }?.let { props.onSelect(it) }
            }
            handlers.style = js("({ cursor: 'pointer' })")
            handlers
        }
    }
}

/**
 * The row's links (issue #1081): to the user's forms and to their client. A click in the cell is the link's, so
 * `onCell` stops it from also opening the row's editor -- as the forms table's workflow cell does.
 */
private fun userLinksColumn(props: UserTableProps): dynamic {
    val c = column("Go to", linksColumn, if (props.offersBecome != null) linksWithBecomeWidth else columnWidths[linksColumn])
    c.onCell = {
        val cellProps: dynamic = js("({})")
        cellProps.onClick = { event: dynamic -> event.stopPropagation() }
        cellProps
    }
    // Read off the row itself -- its key is the user's id, and its client cell the client -- rather than found
    // again among the users, once a row, on every render. Only a caller offered Become, on a test instance, needs the
    // user itself, and a page of users is short.
    c.render = fun(_: dynamic, record: dynamic, _: dynamic): dynamic {
        val id = (record.key as? String)?.toLongOrNull() ?: return null
        return UserLinks.create {
            userId = id
            client = record[USF.client] as? String ?: ""
            carriedClients = props.carriedClients
            showForms = props.showLinks
            showClient = props.showLinks && props.showClientLinks != false
            become = props.offersBecome?.let { offered -> props.users.firstOrNull { it.userId == id }?.takeIf(offered) }
            becomeDisabled = props.becomeDisabled
            onBecomeOutcome = props.onBecomeOutcome
        }
    }
    return c
}

/** What [UserLinks] needs: whose links, and the clients this node carries (which the forms link may choose). */
external interface UserLinksProps : Props {
    var userId: Long
    var client: String
    var carriedClients: Set<String>
    /** Whether the client link is drawn beside the forms one; null reads as true. */
    var showClient: Boolean?

    /** Whether the forms link is drawn; null reads as true. False for a caller offered only [become]. */
    var showForms: Boolean?

    /** The user to offer to become (issue #1511), when they are offered: a [BecomeLink] after the others. */
    var become: AdminUser?

    /** Beside [become]: [BecomeLinkProps.beforeBecome]. */
    var beforeBecome: ((() -> Unit) -> Unit)?

    /** Beside [become]: [BecomeLinkProps.disabled]. */
    var becomeDisabled: Boolean?

    /** Beside [become]: [BecomeLinkProps.onOutcome]. */
    var onBecomeOutcome: (BecomeFailure?) -> Unit
}

/**
 * A user's forms and a user's client, as two links (issue #1081) -- on each row of the list and in the editor of
 * the user who is open. Links, not buttons: each goes somewhere and changes nothing. Then, for a caller with test
 * access, the way to become the user (issue #1511), which is a [BecomeLink].
 */
val UserLinks = FC<UserLinksProps> { props ->
    val formsShown = props.showForms != false
    val clientShown = props.client.isNotEmpty() && props.showClient != false
    span {
        className = ClassName("user-links")
        if (formsShown) {
            a {
                className = ClassName("wf-cell-link")
                href = userFormsHref(props.userId, userFormsSurface(props.client, props.carriedClients))
                title = "The forms this user owns."
                +"Forms"
            }
        }
        if (clientShown) {
            if (formsShown) +" · "
            a {
                className = ClassName("wf-cell-link")
                href = clientDetailHref(props.client)
                title = "The client this user belongs to: ${props.client}."
                +"Client"
            }
        }
        props.become?.let { target ->
            if (formsShown || clientShown) +" · "
            BecomeLink {
                user = target
                beforeBecome = props.beforeBecome
                disabled = props.becomeDisabled
                onOutcome = props.onBecomeOutcome
            }
        }
    }
}

/**
 * Whether the Users page offers [caller] the links from a user to their forms and their client (issue #1081):
 * a client's administrator, of one client or of all. An administrator in `public` without `allClients` administers
 * only their own users (issue #805) -- the forms listing reads them their own rows and the Clients page refuses
 * them -- so for them both links could only fail, and a control that can only fail is not shown. The same people
 * [mayCreateForOthers] names, for the same reason. Pure, covered under `jsNodeTest`.
 */
fun userLinksOffered(caller: UserProfile): Boolean = mayCreateForOthers(caller)

/**
 * A search column's display value for [user] -- the frontend's reading of the row, the counterpart to the
 * backend registry's `AuthUserRow` accessors. The one per-field front-end touch a new column needs, beside its
 * width; everything else (the column itself, its label, its sortability) comes from the shared spec.
 *
 * The `else` returns a marker rather than an empty string, and `UserCellValueTest` asserts every spec field is
 * mapped, so a field added to the spec without a branch here fails the build (a blank column) instead of
 * shipping an empty column silently. Not `private`, so that test can reach it.
 */
fun cellValue(field: String, user: AdminUser, now: Instant): String = when (field) {
    USF.email -> user.primaryId
    // The account's own name; an unnamed account shows the placeholder rather than the username standing in.
    USF.name -> user.name?.takeIf { it.isNotBlank() } ?: "—"
    USF.client -> user.client
    // Which of a person's users this is (issue #750): the persona's label, and the personaSuffix when there is one.
    USF.persona -> personaCell(user.persona, user.personaSuffix, user.actingRoles ?: user.roles)
    // The roles held, and those a sandbox user acts with when they differ (issue #1198).
    USF.roles -> rolesCellText(user)
    // The labels applied (issues #786, #1481), as the read-only editor lists them; most users carry none.
    // Alphabetically, as the column sorts them (issue #1481), so a sorted column reads in order.
    USF.labels -> user.labels.sortedWith(String.CASE_INSENSITIVE_ORDER).joinToString(", ").ifEmpty { "—" }
    // The tracked dates the console shows (issue #462), relative to [now] (issue #1141). A dash rather than a blank:
    // "never" is a fact about the account -- never logged in, never edited -- and an empty cell reads as a rendering
    // failure. An absent registered date means unclaimed (issue #750), which the Status column also says in a word.
    in userTimeFields -> userTimes(user)[field]?.let { formatRelativeTime(it, now) } ?: "—"
    else -> unmappedCell
}

/** The search columns that hold a time (issue #1141). */
val userTimeFields = setOf(USF.lastEdited.at, USF.lastLoggedIn.at, USF.registered.at, USF.activated.at)

/** Each of [userTimeFields] with [user]'s value, as the wire has it; null where the account has none. */
fun userTimes(user: AdminUser): Map<String, String?> = mapOf(
    USF.lastEdited.at to user.lastEditedAt,
    USF.lastLoggedIn.at to user.lastLoggedInAt,
    USF.registered.at to user.registeredAt,
    USF.activated.at to user.activatedAt,
)

/**
 * What a column-header click sorts by: the clicked [field] in the [order] antd reports (`ascend` / `descend`),
 * or -- for the cancel click, which antd reports as a null order -- the console's default order. The order is
 * checked first because antd's cancel names **no field** either: the sorter it hands back has neither, and reading
 * that as "not a sort" was why cancelling did nothing. Null only for an order with no field, which no click sends.
 * Pure, covered under `jsNodeTest`.
 */
fun sortAfterHeaderClick(field: String?, order: String?): Pair<String, Boolean>? = when {
    order == null -> defaultUserSortKey to defaultUserSortDescending
    field == null -> null
    else -> field to (order == "descend")
}

/**
 * The Status column's words. A permanently-deleted tombstone reads as "deleted", not "disabled" -- it is
 * disabled, but saying only that would hide that it is the irreversible kind and cannot be re-enabled. A user
 * nobody has claimed yet reads "unclaimed" (issue #750): the one thing an administrator who provisioned it
 * for somebody else wants to know, and not something the activated date says, since that is stamped at
 * creation. Pure, covered under `jsNodeTest`.
 */
fun statusWords(user: AdminUser): List<String> = buildList {
    add(if (user.deleted) "deleted" else if (user.enabled) "enabled" else "disabled")
    if (!user.registered && !user.deleted) add("unclaimed")
    if (user.hasPassword == true) add("password set")
}

/**
 * The Persona column's value: `Member`, `Admin`, `Member B` -- and, given the user's [roles], what it acts as when
 * that differs from the persona's own (issue #1450): `Member (acts as administrator)`, so a promoted or demoted user
 * does not read as what it was made. Pure, covered under `jsNodeTest`.
 */
fun personaCell(persona: String, personaSuffix: String, roles: Collection<String>? = null): String =
    PERSONA.withActing(personaTerm(persona, personaSuffix), persona, roles)

/** What [cellValue] returns for a spec field with no display branch -- the tell `UserCellValueTest` catches. */
const val unmappedCell = "(?)"

// The columns that are not search fields, so they have no spec to take a name from. Named rather than
// repeated as literals, because each appears three times -- the column, the row's cell, and its width.
private const val idColumn = "userId"
private const val typeColumn = "type"
private const val statusColumn = "status"
private const val linksColumn = "links"

/**
 * Which edge a column is pinned to while the middle scrolls, or null for one that scrolls (issue #750). The
 * columns that say *who* -- id, email, name, client, persona -- hold the left; the one that says whether they
 * are live holds the right; the dates, type, roles, and labels are what you scroll to read. Pure, covered under
 * `jsNodeTest`.
 */
fun pinnedSide(dataIndex: String): String? = when (dataIndex) {
    idColumn, USF.email, USF.name, USF.client, USF.persona -> "left"
    // The links ride with the status: a column after a pinned one is pinned too, or it would slide under it.
    statusColumn, linksColumn -> "right"
    else -> null
}

/**
 * Column widths (a presentation detail, so front-end only) keyed by the spec field name; absent = auto.
 *
 * **Every column declares one now**, because "absent = auto" meant antd divided the leftover space by its own
 * reckoning and gave `Status` -- which holds one short word -- as much as `Roles`, which holds a list. Measured
 * rather than guessed: each figure is the widest content that column actually has to hold, plus its header.
 *
 * The dates are the ones worth stating a reason for. Their content is **bounded** -- a relative time is at
 * most `23h 59m ago` (86px) and past two days a day alone, `2026-08-27` (issue #1141) -- so their figure is the
 * one that holds the widest of those and the header with its sort arrow, not a preference. A column whose content
 * cannot grow should never be the one that wraps. (They were 175 when they held `2026-08-27 19:23 UTC`.)
 *
 * The total was kept near what a 1440-wide window can show, so nothing was pushed off the right-hand edge and
 * behind a scrollbar; the Labels column (issue #1481) takes it past that, which the pinned columns make cheap (see
 * its entry). Above the total the surplus is shared out; below it the table scrolls inside the card. The columns
 * that absorb the difference are the two whose content has no bound.
 */
/** The links column for a caller offered Become too (issue #1511): "Forms · Client · Become", on one line. */
private const val linksWithBecomeWidth = 170

private val columnWidths: Map<String, Int> = mapOf(
    idColumn to 50,
    // The two that genuinely vary, and so the two that give way. An address longer than this wraps, which is
    // the right column to spend a second line on: it is the one whose content has no bound, and the row is
    // clickable if the whole of it is wanted.
    USF.email to 190, USF.name to 130, USF.client to 85,
    // `Member B` at its widest; taken from the email column's share.
    USF.persona to 95,
    // Bounded content, so these are the figures that must not be shaved: `23h 59m ago` or a header with its
    // sort arrow, whichever is wider (issue #1141).
    USF.lastEdited.at to 110, USF.lastLoggedIn.at to 110, USF.registered.at to 110, USF.activated.at to 110,
    // Bounded vocabularies: "Person"/"Business", and "enabled"/"disabled"/"deleted".
    typeColumn to 80, statusColumn to 85,
    // "Forms · Client", which never varies.
    linksColumn to 115,
    // A list, so it is the other one that may wrap -- and now does, on the rows that hold three roles
    // (`user, admin, allClients`, a full administrator) or a long-named one, which are the minority, and on a
    // sandbox user whose row and acting roles differ, which says both (issue #1198) and may take three or four.
    // The 40px it gave up went to the Registered column (issue #750); a few tall rows beat a scrollbar on all.
    USF.roles to 120,
    // Also a list that may wrap, and usually a dash. It takes the table past the 1440 budget above, which the pinned
    // columns make cheap: what scrolls is the middle, and the who and the status stay in view.
    USF.labels to 110,
)

/** Builds an antd column config `{ title, dataIndex, key, width? }`. */
private fun column(title: String, dataIndex: String, width: Int?): dynamic {
    val c: dynamic = js("({})")
    c.title = title
    c.dataIndex = dataIndex
    c.key = dataIndex
    if (width != null) c.width = width
    return c
}

/**
 * The header-click cycle for the column sorting by [sortKey], as antd's `sortDirections` (antd moves to the entry
 * after the current one, and cancels the sort past the last). Most columns take antd's own cycle -- ascend, descend,
 * then cancel, which returns to the default order ([sortAfterHeaderClick]) -- so null. The **default** column's cancel
 * would land on the order it already shows, an offered choice that does nothing; so its cycle starts on the default
 * direction and ends where it began, never reaching the cancel: `descend, ascend, descend` for newest-first. Pure,
 * covered under `jsNodeTest`.
 */
fun userSortCycle(sortKey: String): List<String>? {
    if (sortKey != defaultUserSortKey) return null
    val first = if (defaultUserSortDescending) "descend" else "ascend"
    val second = if (defaultUserSortDescending) "ascend" else "descend"
    return listOf(first, second, first)
}

/**
 * A [column] that sorts on the server: `sorter = true` hands the click to the table's `onChange` rather than
 * reordering locally, and `sortOrder` shows the arrow when this is the active column (null otherwise). The header
 * cycles as [userSortCycle] says: antd's ascend, descend, cancel, except on the default column, which never offers a
 * cancel that would change nothing.
 */
private fun sortableColumn(
    title: String, sortKey: String, width: Int?, activeSort: String, descending: Boolean,
): dynamic {
    val c = column(title, sortKey, width)
    c.sorter = true
    userSortCycle(sortKey)?.let { c.sortDirections = it.toTypedArray() }
    c.sortOrder = if (activeSort == sortKey) (if (descending) "descend" else "ascend") else null
    return c
}
